<template>
  <div id="bookable-box" class="bookable-box wp-100 bd-1 flex flex-column br-8 bd-gray-300-brand sd-1 bg-white overflow-hidden">
    <template v-if="loaded">
      <div class="pa-16 pb-12">
        <div v-if="!dataTrip.is_booked && !isExpired" class="absolute top-16 right-16">
          <DropdownCurrency
            :showCodeCurrent="true"
            classButton="px-8 bd-1 bd-brand-950 br-1000 ml-8"
            classButtonText="fw-700 fs-18 lh-28 mr-4 gray-900 mb-fs-18 mb-lh-28"
          />
        </div>
        <template v-if="dataTrip.is_defined_date && !dataTrip.is_full_payment">
          <div :class="classListContainer">
            <span :class="classPriceLabel">{{ depositText }}</span>
            <span>
              <span :class="classPriceValue">{{ `${currencyDisplay} ${depositAmount}` }}</span>
              <span :class="classNumTraveller">{{ ` for ${dataTrip.pax} ${dataTrip.pax > 1 ? 'travellers' : 'traveller'}` }}</span>
            </span>
          </div>
          <template v-if="!dataTrip.is_fully_paid">
            <div v-if="!dataTrip.is_booked" :class="classListContainer">
              <span :class="classPriceLabel">{{ balanceDueText }}</span>
              <span>
                <template v-if="!dataTrip.is_full_payment">
                  <span :class="classPriceValue">{{ `${currencyDisplay} ${balanceAmount}` }}</span>
                </template>
                <template v-else>
                  <span :class="classPriceValue">{{ `${currencyDisplay} ${balanceAmount}` }}</span>
                  <span :class="classNumTraveller">{{ ` for ${getDynamicString(dataTrip.pax, 'traveller', 'travellers')}` }}</span>
                </template>
              </span>
            </div>
            <template v-else>
              <div v-for="(item, index) in transactionData?.to_be_paid" :class="classListContainer" :key="index">
                <span :class="classPriceLabel">{{ item.label }}</span>
                <span :class="classPriceValue">{{ `${currencyDisplay} ${formatCurrencyNoRound(item.amount)}` }}</span>
              </div>
            </template>
          </template>
        </template>
        <template v-else>
          <div :class="classListContainer">
            <span :class="classPriceLabel">{{ `${!dataTrip.is_defined_date ? 'Payment Amount' : 'Payment Due Now'}` }}</span>
            <span>
              <span :class="classPriceValue">{{ `${currencyDisplay} ${priceDisplay.net}` }}</span>
              <span :class="classNumTraveller">{{ ` for ${getDynamicString(dataTrip.pax, 'traveller', 'travellers')}` }}</span>
            </span>
          </div>
        </template>
        <div class="line-dash mt-8 mb-10"></div>
        <div :class="classListContainer">
          <div :class="classList" v-if='dataTrip.valid_time && dataTrip.is_defined_date && !dataTrip.is_booked'>
            <DJIcon v-if='expiresText.text' :size="20" :color="expiresText.color">
              <IconClockCountdownFill />
            </DJIcon>
            <span v-if="expiresText?.color_text" :style="`color: ${expiresText.color_text}`" v-html="expiresText.text"></span>
            <span v-else v-html="expiresText.text"></span>
            <template v-if='expiresText?.full_time'>
              <CountdownTimer class="fw-600 fs-16 lh-24" :style="`color: ${expiresText.color_text}`" :isWidgetPriceBox="isMobile" />
            </template>
          </div>
          <div :class="classList">
            <DJIcon :size="20" color="#197660">
              <IconCheck />
            </DJIcon>
            <span class="text-md-rg hand underline" h="view-terms-conditions" @click="emit('openTermBox')">
              {{ dataTrip?.cancellation_policy?.has_policy ? 'Custom Terms and Conditions Applied' : 'Terms and Conditions Applied' }}
            </span>
          </div>
        </div>
      </div>
      <div :class="[classListContainer, 'bg-gray-50 py-10 px-16 grid-gap-8']">
        <div class="gray-700 text-md-rg">Travel Information</div>
        <div class="flex grid-gap-8" v-for="(item, index) in dataTrip?.trip_benefits" :key="index">
          <DJIcon :size="20" color="#197660" class="mt-2">
            <IconCheck />
          </DJIcon>
          <span class="text-md-rg">{{ item }}</span>
        </div>
        <div :class='classList' v-if='!isEmpty(dataTrip?.travel_time)'>
          <DJIcon :size="20" color="#197660" class="mt-2">
            <IconCheck />
          </DJIcon>
          <span class='lh-24'>{{ dataTrip?.travel_time }}</span>
        </div>
        <div class="flex grid-gap-8">
          <DJIcon :size="20" color="#197660">
            <IconCheck />
          </DJIcon>
          <span class="underline hand" @click.prevent="$emit('scrollToInclusions')">See All Inclusions</span>
        </div>
        <div class="line-dash"></div>
        <span class="text-md-rg mb-text-sm-rg gray-700">Designed by</span>
        <SectionLocalDesigner v-if="designer" :designer-id="designer?.id" :designer-info="designer" />
      </div>
      <div class="px-16 py-10" v-if="!dataTrip.is_fully_paid">
        <DJButton
          v-if="!dataTrip.is_defined_date"
          id="button-bookable-trip-plan-confirm-travel-date"
          class="wp-100"
          type="accent"
          :suffix-icon="IconCaretRight"
          size="large"
          @click.prevent="$emit('confirmDate')"
        >
          Confirm Your Travel Date
        </DJButton>
        <TripButton
          v-else
          button-id-prefix="button-bookable-trip-plan"
          customClass="wp-100"
          :show-suffix-icon="true"
          @customTrip="emit('customTrip')"
          @handleBook="handleBook"
        />
        <div class="tc pt-8 fw-500 fs-14 lh-20 gray-700">Make your next trip full of lifelong memories.</div>
      </div>
    </template>
    <div class="flex items-center justify-center" v-else>
      <Loader class="w-50 h-50" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject, onMounted, onUnmounted } from "vue"
import TripButton from '~/components/pages/TripPlanDetail/sections/SectionTripPriceBox/TripButton.vue'
import { daysBetween, formatCurrencyNoRound, formatDate, isEmpty } from '~/helper'
import tripApi from '~/composables/api/tripApi'
import DropdownCurrency from '~/components/globals/Header/elements/DropdownCurrency/DropdownCurrency.vue'
import CountdownTimer from '~/components/sections/SectionCountdown/CountdownTimer.vue'
import Loader from '~/components/globals/Loading/Loader.vue'
import DJIcon from '~/components/ui/icon/Icon.vue'
import DJButton from '~/components/ui/button/Button.vue'
import SectionLocalDesigner from '~/components/pages/TripPlanDetail/sections/SectionLocalDesigner/SectionLocalDesigner.vue'
import { IconCheck, IconClockCountdownFill, IconCaretRight } from '~/components/ui/icon/icons'
import { PHONE } from '~/constant/common'
import { useCurrenciesStore } from '~/store/pinia/currencies'
import { useTripPlanStore } from '~/store/pinia/tripPlan'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

const classPriceLabel = 'gray-700 text-md-rg'
const classPriceValue = 'heading-5'
const classNumTraveller = 'text-lg-sb'
const classListContainer = 'flex flex-column grid-gap-4'
const classList = 'flex items-center grid-gap-8 fs-16 lh-24'
const emit = defineEmits(['openTermBox', 'handleBook', 'customTrip', 'confirmDate', 'scrollToInclusions'])

const loaded = ref(false)
const currencyDisplay = inject('currencyDisplay', 'AU$')
const isExpired = inject('isExpired', false)
const currenciesStore = useCurrenciesStore()
const tripPlanStore = useTripPlanStore()
const generalStore = useGeneralInfoStore()

const dataTrip = computed(() => tripPlanStore.dataTrip)
const designer = computed(() => tripPlanStore.designer)
const priceDisplay = computed(() => tripPlanStore.priceDisplayAPI)
const isCompleted = computed(() => tripPlanStore.isCompleted)
const currencySelected = computed(() => currenciesStore.currencySelected /*store.state.currenciesStore.currencySelected*/)
const transactionData = computed(() => tripPlanStore.transactionData)
const daysTillExpired = computed(() => {
  const now = new Date();
  const nowUTC = new Date(now.toUTCString());
  return daysBetween(nowUTC, new Date(dataTrip.value?.valid_time))
})
const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isMobile = computed(() => windowWidth.value <= PHONE)
const expiresText = computed(() => {
  const now = new Date();
  const nowUTC = new Date(now.toUTCString());

  if (isExpired?.value) {
    return {
      color: '#191919',
      text: `<span class='fw-600'>Offer expired</span>`,
      full_time: false
    }
  }
  if (isCompleted.value) {
    return {
      color: '#197660',
      text: 'Completed',
      full_time: false
    }
  }
  if (daysBetween(nowUTC, new Date(dataTrip.value?.valid_time), true) >= 2) {
    return {
      color: '#197660',
      text: `Expires in <span class='fw-600'>${daysTillExpired.value} day${
        daysTillExpired.value >= 2 ? 's' : ''}</span>`,
      full_time: false
    }
  } else {
    return {
      color: '#F79009',
      color_text: '#F79009',
      text: 'Expires in',
      full_time: true
    }
  }
})
const depositText = computed(() => {
  if (!isEmpty(transactionData?.value)) {
    return `Amount Paid`
  }
  if (isExpired.value) {
    return 'Deposit Due Now'
  }
  return `Deposit Now`
})
const depositAmount = computed(() => {
  let remainingPrice = priceDisplay.value.deposit
  if (transactionData?.value?.paid_on) {
    remainingPrice = transactionData?.value?.paid_on?.reduce((a, b) => {
      return a + b.amount
    }, 0)
    remainingPrice = formatCurrencyNoRound(remainingPrice)
  }
  return remainingPrice
})

const balanceDueText = computed(() => {
  return `Balance Due On ${formatDate(dataTrip.value.due_date, 'd m Y')}`
})

const balanceAmount = computed(() => {
  let remainingPrice = priceDisplay.value.balance_due
  if (transactionData?.value?.to_be_paid) {
    remainingPrice = transactionData?.value?.to_be_paid?.reduce((a, b) => {
      return a + b.amount
    }, 0)
    remainingPrice = formatCurrencyNoRound(remainingPrice)
  }
  return remainingPrice
})

function handleBook(data) {
  emit('handleBook', data)
}

async function getConfirmedTravellers() {
  try {
    const { data } = await tripApi.getTripPlanSettings(dataTrip.value.id, {
      currency_code: currencySelected.value.code,
    })

    // store.commit('tripPlanStore/setGroupBookingData', {
    //   confirmed_travellers: data.data.confirmed_travellers,
    //   confirmed_traveller_details: data.data.confirmed_traveller_details,
    // })
    tripPlanStore.setGroupBookingData({
      confirmed_travellers: data.data.confirmed_travellers,
      confirmed_traveller_details: data.data.confirmed_traveller_details,
    })
  } catch (error) {
    console.log(error)
  }
}

function getDynamicString(number, singular, plural) {
  return `${number} ${number > 1 ? plural : singular}`
}

watch(transactionData, (newValue) => {
  if (newValue) {
    loaded.value = true
  }
})

onMounted(async () => {
  try {
    if (!dataTrip.value.is_booked || dataTrip.value.id === transactionData.value.trip_id) {
      loaded.value = true
    }
    await getConfirmedTravellers()
  } catch (error) {
    console.log(error)
  }
})

onUnmounted(() => {
  loaded.value = false
  // store.commit('tripPlanStore/setTransactionData', {})
  tripPlanStore.setTransactionData({})
})
</script>

<style lang="scss" scoped>
.bookable-box {
  &__link {
    color: $link; // Wave 5B — legacy blue link → brand green (v2 Link)
    text-decoration: underline !important;
  }
  // W7 dark: currency-dropdown chevron is an <img> (icon-chevron-down-v2.svg, stroke #0A0A0A) invisible on the dark #0D0D0D box → invert to near-white (same as PriceBoxTripPlan; scoped to this box so the shared DropdownCurrency's light-kept / #topInfo chevrons are untouched)
  [data-theme='dark'] & :deep(img.icon) {
    filter: brightness(0) invert(1);
  }

  // W7 dark: the currency dropdown PANEL is a hardcoded-white %header-dropdown (kept literal so the
  // LIGHT-KEPT checkout/enquiry currency dropdowns stay correct). On this dark price box it opens
  // onto #0D0D0D, so repaint it to the dark Dropdown Menu spec (panel #0D0D0D, 1px #2E2E2E border,
  // #FAFAFA rows; gold hover/selected fill carries over, label → #0A0A0A per V30). Same fix as
  // PriceBoxTripPlan; scoped to .bookable-box + [data-theme='dark'] → light stays byte-identical.
  [data-theme='dark'] & :deep(.right-currencies--dropdown) {
    background-color: $surface; // #0D0D0D (was white)
    border-color: $border;      // #2E2E2E (was $border-hairline)

    ul li button {
      color: $foreground; // #FAFAFA rows (was $ink → invisible on the dark panel)

      &:hover,
      &:focus-visible,
      &[aria-selected='true'] {
        color: $ink; // gold fill carries over; label → ink per V30
      }
    }
  }
}
</style>
